import type { Building } from "building-types/building/buildingTypes";
import type { Casework } from "building-types/casework/caseworkTypes";
import type {
  WallCasework,
  WallCaseworkDynamicDeprecated,
  WallCaseworkFixed,
  WallCaseworkFixedDeprecated,
} from "building-types/casework/wallCaseworkTypes";
import type { EntityInstance } from "building-types/roomLayouts/roomLayoutTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { InteriorFloorMaterialOverride } from "building-types/settings/floorMaterialSettings";
import type { RWTable } from "common/data/table";
import uid from "common/utils/uid";
import { resolveCaseworkFromSettings } from "doc/elements/caseworkSettingsResolution";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { assertIsNotFormula } from "doc/formula/formulaUtils";
import { buildRoomLayoutInstancesByRoomLayoutId, entityInstancesForEntity } from "doc/rooms/layout/roomLayoutTools";
import { caseworkCoversFloor, isFixedCasework } from "doc/utils/caseworkDescriptorUtils";
import { makeCasework } from "../generationTools/generateCaseworkTools";
import { copyEntityForComponentsWithOptions } from "../utils/componentInstanceUtils";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

export function selectCasework({
  doc: {
    casework,
    optionSets,
    instanceOptionSets,
    roomLayouts,
    roomLayoutInstances,
    entityRoomLayouts,
    rooms: docRooms,
  },
  optionChoices: buildingOptionChoices,
  mirror,
  rooms,
  roomIndex,
  roomOutlineTable,
  roomFloorAssemblies,
  walls,
  wallMasses,
  settings,
  entityDisabledByGroup,
  enabledFeatures,
  relatedRoomsMap,
}: Pick<
  Building,
  | "doc"
  | "optionChoices"
  | "mirror"
  | "rooms"
  | "roomIndex"
  | "roomOutlineTable"
  | "roomFloorAssemblies"
  | "walls"
  | "wallMasses"
  | "settings"
  | "entityDisabledByGroup"
  | "enabledFeatures"
  | "relatedRoomsMap"
>) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    docRooms,
    rooms,
    docRoomLayouts: roomLayouts,
    roomLayoutInstances,
    docEntityRoomLayouts: entityRoomLayouts,
    roomIndex,
    roomOutlineTable,
    roomFloorAssemblies,
    docCasework: casework,
    mirror,
    optionChoices: resolveOptionsInFormula(casework, definitions, buildingOptionChoices),
    rawOptionChoices: buildingOptionChoices,
    definitions,
    walls,
    wallMasses,
    caseworkCatalog: settings.caseworkCatalog.caseworkTypes,
    caseworkSettings: settings.casework,
    entityDisabledByGroup,
    componentsEnabled: enabledFeatures.modelComponents,
    relatedRoomsMap,
  };
}

const compare = makeGenerationStageComparator<typeof selectCasework>({
  definitions: "shallow",
  docRooms: "shallow",
  optionChoices: "shallow",
  relatedRoomsMap: "shallow",
});

export function generateCasework(
  {
    docCasework,
    docRoomLayouts,
    docRooms,
    roomLayoutInstances,
    docEntityRoomLayouts,
    optionChoices,
    rawOptionChoices,
    definitions,
    mirror,
    rooms,
    roomIndex,
    roomOutlineTable,
    roomFloorAssemblies,
    walls,
    wallMasses,
    caseworkCatalog,
    caseworkSettings,
    entityDisabledByGroup,
    componentsEnabled,
    relatedRoomsMap,
  }: ReturnType<typeof selectCasework>,
  forEveryCasework: ((caseworkId: string, casework: Casework, disabled: boolean) => void) | null = null,
): Pick<Building, "casework" | "issues"> {
  const issues: BuildingGenerationIssue[] = [];
  const wallLayerExtentsCache: WallLayerExtentsCache = {};
  const casework: RWTable<Casework> = {};
  const hasRoomLayouts = !!Object.keys(docEntityRoomLayouts).length;
  const roomLayoutInstancesByRoomLayoutId = hasRoomLayouts
    ? buildRoomLayoutInstancesByRoomLayoutId(roomLayoutInstances)
    : undefined;

  for (const wallCaseworkId in docCasework) {
    const rawWallCasework = docCasework[wallCaseworkId];
    const wallCasework = evalFormulas(rawWallCasework, (id) => optionChoices[id], undefined, entityDisabledByGroup);
    if (wallCasework.disabled && !forEveryCasework && !componentsEnabled) {
      continue;
    }

    const caseworkInstances = entityInstancesForEntity({
      hasRoomLayouts,
      docEntityRoomLayouts,
      docRoomLayouts,
      activeRoomLayoutInstances: roomLayoutInstances,
      roomLayoutInstancesByRoomLayoutId,
      entity: wallCasework,
      rooms: docRooms, // the original rooms referenced by room layouts and room layout instances
    });

    // Combine room layout instances and component instances into one unified array
    const allInstances: EntityInstance[] = [];
    const componentCaseworkByInstanceId = new Map<string, WallCasework>();
    // Populate allInstances and componentCaseworkByInstanceId with archetype (caseworkInstances) and component instance placements
    copyEntityForComponentsWithOptions(
      rawWallCasework,
      relatedRoomsMap,
      caseworkInstances,
      definitions,
      rawOptionChoices,
      allInstances,
      componentCaseworkByInstanceId,
      roomLayoutInstances,
      !!forEveryCasework,
    );

    // Single loop handles both room layout instances and component instances
    for (const {
      entityInstanceType,
      parentId,
      edgeId,
      edgeIds,
      handleOverride,
      mirrored,
      instanceId,
    } of allInstances) {
      const entityRoom = parentId && rooms[parentId];
      if (!entityRoom) {
        continue;
      }

      const caseworkId = uid();

      const { wallIndex } = walls[entityRoom.level.id];

      const wallCaseworkSource = instanceId
        ? componentCaseworkByInstanceId.get(instanceId) ?? wallCasework
        : wallCasework;

      if (wallCaseworkSource.disabled && !forEveryCasework) {
        continue;
      }

      const caseworkInstance: WallCasework = !!edgeIds
        ? {
            ...(wallCaseworkSource as WallCaseworkDynamicDeprecated),
            parentId,
            edgeIds,
            id: caseworkId,
            entityInstanceType,
            handleOverride,
          }
        : {
            ...(wallCaseworkSource as WallCaseworkFixed | WallCaseworkFixedDeprecated),
            parentId,
            edgeId: edgeId ?? "",
            id: caseworkId, // casework id is overwritten later (keeping for now)
            entityInstanceType,
            handleOverride,
          };

      const { floorMaterialOverride, verticalOffset } = wallCaseworkSource;

      assertIsNotFormula(verticalOffset);

      // An absent offset inherits from Casework Settings, and `undefined <= 0` is false — so reading it
      // raw would stop a newly created base cabinet from clearing the floor material beneath it.
      const resolvedVerticalOffset =
        verticalOffset ?? resolveCaseworkFromSettings(caseworkInstance.caseworkType, caseworkSettings).verticalOffset;

      let catalogFloorMaterialOverride: InteriorFloorMaterialOverride | undefined;
      if (isFixedCasework(wallCaseworkSource) && wallCaseworkSource.catalogDescriptorId) {
        const catalogDescriptor = caseworkCatalog[wallCaseworkSource.catalogDescriptorId];
        catalogFloorMaterialOverride = catalogDescriptor?.floorMaterialOverride;
      }
      let floorMaterial: InteriorFloorMaterialOverride | undefined =
        floorMaterialOverride ?? catalogFloorMaterialOverride;
      if (
        caseworkSettings.removeFloorMaterialUnderBaseCabinets &&
        !floorMaterial &&
        caseworkCoversFloor(caseworkInstance.caseworkType)
      ) {
        floorMaterial = resolvedVerticalOffset <= 0 ? "NONE" : floorMaterialOverride;
      }

      makeCasework({
        wallCasework: caseworkInstance,
        wallLayerExtentsCache,
        wallIndex,
        wallMasses,
        room: entityRoom,
        roomIndex,
        roomOutlines: roomOutlineTable[entityRoom.id],
        roomFloorAssemblies,
        floorMaterial,
        caseworkCatalog,
        issues,
        caseworkSettings,
        mirror: mirror !== mirrored,
        wallCaseworkId,
        forEveryCasework,
        outputCasework: casework,
      });
    }
  }

  return { casework, issues };
}

export default memo(selectCasework, generateCasework, compare);
